<!--
    QL workbench — ONE page, TWO modes (builder | playground). This file is the SHELL only.

    SLOTS (keep the template names):
      #builderSlot    LEFT  "Builder"   -> presets (playground) + mvs-ql-builder-panel + ui-ql-widget-type-editor
      #chatSlot       LEFT  "Chat"      -> ui-ql-assist-chat (LLM chat designer)
      #previewSlot    RIGHT "Vorschau"  -> ui-ql-widget-preview (model + currentValidation$ verdict, no own validate)
      #jsonSlot       RIGHT "QL-JSON"   -> ui-ql-json-view (playground only)
      #inspectorSlot  RIGHT "Inspektor" -> ui-ql-inspector (playground only)
    Every slot reads/writes the per-page UiQlWidgetWorkbenchStateService (inject it; it is provided by
    this page). The slot templates are declared at the TOP LEVEL of this file so each one is edited in
    exactly one place, and rendered through *ngTemplateOutlet inside the panes.
-->

<!-- PAGE HEADER (template mode) - MUST stay outside any @if/@switch so @ViewChild resolves. -->
<ng-template #headerTemplate>
    <div class="ui-ql-workbench-header flex flex-wrap align-items-center justify-content-between gap-3 surface-0 px-4 py-3 border-bottom-1 surface-border">
        <div class="flex align-items-center gap-3">
            <i class="fa-solid text-2xl text-primary"
               [class.fa-flask]="mode === 'playground'"
               [class.fa-cubes]="mode === 'builder'"></i>
            <div class="flex flex-column">
                <span class="text-xl font-semibold">{{ mode === 'playground' ? 'QL-Playground' : 'Widget-Builder' }}</span>
                @if (state.meta$ | async; as meta) {
                    @if (mode === 'builder' && meta.uiWidgetId != null) {
                        <span class="text-sm text-color-secondary">{{ meta.name }} · {{ meta.alias }}</span>
                    }
                }
            </div>
            @if (mode === 'builder' && (state.meta$ | async)?.hidden) {
                <p-tag value="deaktiviert" severity="danger" icon="fa-regular fa-ban" [rounded]="true"
                       pTooltip="Das Widget ist deaktiviert und wird nicht mehr angeboten. Speichern ändert daran nichts."
                       data-test="ui-ql-workbench-hidden"></p-tag>
            }
            <!-- playground has nothing to save, so "unsaved" would be noise there (QA #21) -->
            @if (mode === 'builder' && (state.dirty$ | async)) {
                <p-tag value="Ungespeicherte Änderungen" severity="secondary" [rounded]="true"
                       data-test="ui-ql-workbench-dirty"></p-tag>
            }
            @if ((state.currentValidation$ | async)?.envBound) {
                <p-tag value="umgebungsgebunden" severity="warn" [rounded]="true"
                       pTooltip="Die Definition enthält umgebungsabhängige IDs und ist nicht zwischen Umgebungen übertragbar."></p-tag>
            }
        </div>

        @if (access === 'granted') {
            <div class="flex align-items-center gap-2">
                <p-button label="Zurücksetzen"
                          icon="fa-solid fa-rotate-left"
                          severity="secondary"
                          [outlined]="true"
                          (onClick)="resetWorkbench()"></p-button>
                @if (mode === 'playground') {
                    <p-button label="Als Widget speichern"
                              icon="fa-solid fa-share-from-square"
                              [disabled]="!(state.model$ | async)"
                              (onClick)="switchToBuilder()"></p-button>
                } @else {
                    @if (state.meta$ | async; as meta) {
                        @if (meta.uiWidgetId != null) {
                            @if (meta.hidden) {
                                <p-button label="Aktivieren"
                                          icon="fa-solid fa-eye"
                                          severity="success"
                                          [outlined]="true"
                                          [loading]="visibilityBusy"
                                          pTooltip="Das Widget wird wieder zum Hinzufügen angeboten."
                                          tooltipPosition="bottom"
                                          data-test="ui-ql-workbench-activate"
                                          (onClick)="activateWidget()"></p-button>
                            } @else {
                                <p-button label="Deaktivieren"
                                          icon="fa-solid fa-eye-slash"
                                          severity="danger"
                                          [outlined]="true"
                                          [loading]="visibilityBusy"
                                          pTooltip="Das Widget wird nicht mehr zum Hinzufügen angeboten; platzierte Kacheln bleiben."
                                          tooltipPosition="bottom"
                                          data-test="ui-ql-workbench-deactivate"
                                          (onClick)="deactivateWidget()"></p-button>
                            }
                        }
                    }
                    <p-button label="Speichern"
                              icon="fa-solid fa-floppy-disk"
                              [disabled]="!(state.canSave$ | async) || saveBusy"
                              [pTooltip]="(state.canSave$ | async) ? '' : 'Speichern ist erst mit einer gültigen Definition möglich.'"
                              tooltipPosition="bottom"
                              (onClick)="openSaveDialog()"></p-button>
                }
            </div>
        }
    </div>
</ng-template>

<!-- ================================================================== -->
<!-- SLOTS                                                               -->
<!-- ================================================================== -->

<ng-template #builderSlot>
    <div class="flex flex-column gap-3">
        @if (mode === 'playground') {
            <ui-ql-preset-select (presetLoad)="onPresetLoad($event)"></ui-ql-preset-select>
        }
        <mvs-ql-builder-panel [model]="state.model$ | async"
                              (modelChange)="onModelChange($event)"></mvs-ql-builder-panel>
        <ui-ql-widget-type-editor [model]="state.model$ | async"
                                  [widgetType]="(state.widgetType$ | async) ?? 'table'"
                                  [mapping]="(state.mapping$ | async) ?? {}"
                                  (widgetTypeChange)="state.setWidgetType($event)"
                                  (mappingChange)="state.setMapping($event)"
                                  (modelChange)="onModelChange($event)"></ui-ql-widget-type-editor>
    </div>
</ng-template>

<ng-template #chatSlot>
    <!-- LLM chat designer (requirement.md "Erweiterung: LLM-Chat-Designer"); reads/writes the page state. -->
    <ui-ql-assist-chat></ui-ql-assist-chat>
</ng-template>

<ng-template #previewSlot>
    @if (previewInput$ | async; as input) {
        <!-- ALL four from the held input - never type/mapping from the live state next to a held model (QA #8) -->
        <ui-ql-widget-preview [model]="input.model"
                              [policyViolations]="input.policyViolations"
                              [widgetType]="input.widgetType"
                              [mapping]="input.mapping"
                              (stateChange)="onPreviewStateChange($event)"
                              (statusChange)="onPreviewStatusChange($event)"></ui-ql-widget-preview>
    } @else {
        <div class="flex flex-column gap-2">
            <p-skeleton height="2rem" width="40%"></p-skeleton>
            <p-skeleton height="14rem"></p-skeleton>
        </div>
    }
</ng-template>

<ng-template #jsonSlot>
    <ui-ql-json-view [qlQuery]="state.qlQuery$ | async"
                     [draft]="jsonDraft"
                     (applyModel)="onJsonApply($event)"></ui-ql-json-view>
</ng-template>

<ng-template #inspectorSlot>
    <ui-ql-inspector [previewState]="previewState"
                     [previewStatus]="previewStatus"
                     [validation]="state.currentValidation$ | async"
                     [validating]="(state.validating$ | async) ?? false"
                     [validationError]="(state.state$ | async)?.validationError ?? null"></ui-ql-inspector>
</ng-template>

<!-- ================================================================== -->
<!-- BODY                                                                -->
<!-- ================================================================== -->

@switch (access) {

    @case ('checking') {
        <div class="flex flex-column gap-3 p-3" aria-busy="true" aria-label="Berechtigung wird geprüft …">
            <p-skeleton height="2.5rem" width="40%"></p-skeleton>
            <div class="flex gap-3">
                <p-skeleton height="20rem" styleClass="flex-1"></p-skeleton>
                <p-skeleton height="20rem" styleClass="flex-1"></p-skeleton>
            </div>
            <span class="text-sm text-color-secondary">Berechtigung wird geprüft …</span>
        </div>
    }

    @case ('denied') {
        <div class="flex flex-column align-items-center justify-content-center text-center py-8">
            <i class="fa-solid fa-lock text-4xl text-color-secondary mb-3"></i>
            <span class="text-xl font-semibold">Keine Berechtigung</span>
            <span class="mt-2 text-color-secondary">Für den Widget-Builder und das QL-Playground ist das Schreibrecht auf UI-Widgets erforderlich.</span>
        </div>
    }

    @case ('unknown') {
        <div class="flex flex-column align-items-center justify-content-center text-center py-8">
            <i class="fa-solid fa-triangle-exclamation text-4xl text-color-secondary mb-3"></i>
            <span class="text-xl font-semibold">Berechtigung nicht prüfbar</span>
            <span class="mt-2 text-color-secondary">Die Berechtigung konnte nicht geprüft werden. Bitte die Seite neu laden.</span>
        </div>
    }

    @default {
        @if (loadError) {
            <p-message severity="error" [text]="loadError" styleClass="w-full mb-3"></p-message>
        }
        @if (unmappable.length) {
            <p-message severity="error" styleClass="w-full mb-3">
                <div class="flex flex-column">
                    <span class="font-medium mb-2">Die gespeicherte Definition kann im Builder nicht vollständig dargestellt werden:</span>
                    <ul class="m-0 pl-4">
                        @for (message of unmappable; track $index) {
                            <li>{{ message }}</li>
                        }
                    </ul>
                </div>
            </p-message>
        }

        <!-- Height: autoFitViewport measures the host top at runtime (fe_core_review-workbench R5);
             152px is only the first-paint approximation; 2rem = .layout-content padding-bottom.
             This page adds NO padding of its own below the host - keep viewportBottomGapRem in sync if that changes. -->
        <mvs-review-workbench [panelSizes]="[42, 58]"
                              [minSizes]="[28, 30]"
                              [viewportOffsetPx]="152"
                              [autoFitViewport]="true"
                              [viewportBottomGapRem]="2">

            <!-- PER-PANE SCROLL (fe_core_review-workbench R5, the consumer's half of the contract): the shared
                 component gives each pane a height-bound flex column (.rw-col) and CLIPS whatever overflows it
                 (.p-splitterpanel overflow:hidden). The projected content must therefore bring ITS OWN scroll root:
                 wrapper = h-full flex-column min-h-0 (fills the column, may shrink), tab bar = fixed,
                 content = flex-auto min-h-0 overflow-y-auto (the ONE scroll root of the pane). min-h-0 is
                 load-bearing: a flex item's default min-height:auto refuses to shrink below its content, so
                 without it the content grows past the pane and is cut off with no scrollbar (measured: 1306px
                 of content in an 825px pane, wheel did nothing). min-h-full is NOT a substitute - it is a floor. -->

            <!-- LEFT: builder area ("Builder | Chat") -->
            <ng-template #workbenchList>
                <div class="h-full flex flex-column min-h-0">
                    <p-tabs [value]="(state.leftTab$ | async) ?? 'builder'"
                            (valueChange)="setLeftTab($any($event))">
                        <p-tablist>
                            <p-tab value="builder"><i class="fa-solid fa-diagram-project mr-2"></i>Builder</p-tab>
                            <p-tab value="chat"><i class="fa-solid fa-comments mr-2"></i>Chat</p-tab>
                        </p-tablist>
                    </p-tabs>
                    <div class="flex-auto min-h-0 overflow-y-auto p-3">
                        @if (loadingDefinition) {
                            <div class="flex flex-column gap-3" aria-busy="true" aria-label="Widget wird geladen …">
                                <p-skeleton height="2.5rem"></p-skeleton>
                                <p-skeleton height="8rem"></p-skeleton>
                                <p-skeleton height="8rem"></p-skeleton>
                                <span class="text-sm text-color-secondary">Widget wird geladen …</span>
                            </div>
                        } @else {
                            @switch (state.leftTab$ | async) {
                                @case ('chat') {
                                    <ng-container *ngTemplateOutlet="chatSlot"></ng-container>
                                }
                                @default {
                                    <ng-container *ngTemplateOutlet="builderSlot"></ng-container>
                                }
                            }
                        }
                    </div>
                </div>
            </ng-template>

            <!-- RIGHT: preview (builder) / "Vorschau | QL-JSON | Inspektor" (playground).
                 The PANE is always mounted; only its content depends on the mode (R3). -->
            <ng-template #workbenchDetail>
                <div class="h-full flex flex-column min-h-0">
                    @if (mode === 'playground') {
                        <p-tabs [value]="(state.rightTab$ | async) ?? 'preview'"
                                (valueChange)="setRightTab($any($event))">
                            <p-tablist>
                                <p-tab value="preview"><i class="fa-solid fa-chart-simple mr-2"></i>Vorschau</p-tab>
                                <p-tab value="json"><i class="fa-solid fa-code mr-2"></i>QL-JSON</p-tab>
                                <p-tab value="inspector"><i class="fa-solid fa-magnifying-glass-chart mr-2"></i>Inspektor</p-tab>
                            </p-tablist>
                        </p-tabs>
                        <!-- The preview stays MOUNTED in every tab (only hidden): the inspector shows what it
                             executed, and switching tabs must not re-run the query. -->
                        <div class="flex-auto min-h-0 overflow-y-auto p-3">
                            @let rightTab = (state.rightTab$ | async) ?? 'preview';
                            <div [class.hidden]="rightTab !== 'preview'">
                                <ng-container *ngTemplateOutlet="previewSlot"></ng-container>
                            </div>
                            @if (rightTab === 'json') {
                                <ng-container *ngTemplateOutlet="jsonSlot"></ng-container>
                            }
                            @if (rightTab === 'inspector') {
                                <ng-container *ngTemplateOutlet="inspectorSlot"></ng-container>
                            }
                        </div>
                    } @else {
                        <div class="flex-auto min-h-0 overflow-y-auto p-3">
                            <ng-container *ngTemplateOutlet="previewSlot"></ng-container>
                        </div>
                    }
                </div>
            </ng-template>

        </mvs-review-workbench>

        @if (mode === 'builder') {
            <ui-ql-widget-save-dialog [(visible)]="saveDialogVisible"
                                      [initialValue]="saveDialogInitialValue"
                                      [alias]="(state.meta$ | async)?.alias ?? null"
                                      [busy]="saveBusy"
                                      [violations]="saveViolations"
                                      [errorMessage]="saveErrorMessage"
                                      (onSave)="onSaveDialogSubmit($event)"></ui-ql-widget-save-dialog>
        }
    }
}
